import { cn } from '~/lib/utils';
import type { Budget, BudgetSource, BudgetTimeframe } from '~/hooks/queries/use-budget';
import { Tip } from '~/components/ui/tip';

/**
 * Shared presentation for the platform's compute budget (FRONT-1194).
 *
 * The platform also ships a hex colour per warning threshold. We deliberately
 * ignore it: the app has one green and one red across every surface, and
 * letting a tenant's stored hex leak in would put a fourth amber and a second
 * red next to our own chips. Tone comes from the thresholds' percentages,
 * which is the part that carries meaning.
 */

/** Compute blocks run to eight figures; nobody reads 100000000. */
export function formatBlocks(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m >= 10 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(Math.round(n));
}

export type BudgetTone = 'ok' | 'warn' | 'crit';

/**
 * Tone from consumption. Uses the tenant's own warning thresholds when they
 * set any, so the colour changes where the org said it should, and falls back
 * to a plain 75/100 split when they haven't configured warnings.
 */
export function budgetTone(budget: Budget): BudgetTone {
  if (budget.limitReached) return 'crit';
  const pct = budget.fractionUsed * 100;
  const thresholds = budget.warnings.thresholds.map((t) => t.percent);
  const highest = thresholds.length ? Math.max(...thresholds) : 0;
  const lowest = thresholds.length ? Math.min(...thresholds) : 0;
  if (highest && pct >= highest) return 'crit';
  if (lowest && pct >= lowest) return 'warn';
  if (!thresholds.length && pct >= 75) return 'warn';
  return 'ok';
}

const FILL: Record<BudgetTone, string> = {
  ok: 'bg-success',
  warn: 'bg-amber-500',
  crit: 'bg-destructive',
};

const TEXT: Record<BudgetTone, string> = {
  ok: 'text-success',
  warn: 'text-amber-500',
  crit: 'text-destructive',
};

export function budgetToneText(tone: BudgetTone): string {
  return TEXT[tone];
}

const TIMEFRAME_NOUN: Record<BudgetTimeframe, string> = {
  monthly: 'this month',
  weekly: 'this week',
  daily: 'today',
};

export function timeframeNoun(timeframe: BudgetTimeframe): string {
  return TIMEFRAME_NOUN[timeframe];
}

/** Plain-language explanation of where a budget came from. */
export function describeSource(source: BudgetSource): string | null {
  switch (source) {
    case 'override':
      return 'Your workspace set this budget for you specifically.';
    case 'group':
      return 'Your budget comes from a group you belong to.';
    case 'default':
      return "This is your workspace's default budget.";
    case 'system_default':
      return 'No budget was set for your workspace, so the standard one applies.';
  }
}

/** When the period rolls over, in words. */
export function describeReset(periodResetsAt: string | null): string | null {
  if (!periodResetsAt) return null;
  const resets = new Date(periodResetsAt);
  if (Number.isNaN(resets.getTime())) return null;
  const hours = (resets.getTime() - Date.now()) / 3_600_000;
  if (hours <= 0) return 'resets shortly';
  if (hours < 1) return 'resets within the hour';
  if (hours < 24) return `resets in ${Math.round(hours)}h`;
  const days = Math.round(hours / 24);
  return `resets in ${days} day${days === 1 ? '' : 's'}`;
}

/**
 * The meter itself. `markerPercent` draws the base budget on a topped-up
 * bar, so it's visible that the extra headroom was granted rather than
 * configured.
 */
export function BudgetBar({
  budget,
  className,
}: {
  budget: Budget;
  className?: string;
}) {
  const tone = budgetTone(budget);
  const pct = Math.min(100, Math.max(0, budget.fractionUsed * 100));
  const markerPercent =
    budget.extensionCb > 0 && budget.limit > 0
      ? (budget.baseLimit / budget.limit) * 100
      : null;

  return (
    <div
      className={cn('relative h-1.5 w-full overflow-hidden rounded-sm bg-muted', className)}
      role="meter"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Compute budget: ${Math.round(pct)}% used`}
    >
      <div
        className={cn('h-full rounded-sm transition-[width] duration-300', FILL[tone])}
        style={{ width: `${pct}%` }}
      />
      {markerPercent !== null && (
        <Tip label="Your budget before the top-up">
          <span
            className="absolute inset-y-0 w-px bg-foreground/40"
            style={{ left: `${Math.min(100, markerPercent)}%` }}
          />
        </Tip>
      )}
    </div>
  );
}

/**
 * Compact form for the sidebar. Stays quiet until the first warning threshold
 * so a healthy budget is not a permanent piece of furniture drawing the eye.
 */
export function SidebarBudget({ budget }: { budget: Budget }) {
  const tone = budgetTone(budget);
  if (tone === 'ok') return null;

  const pct = Math.round(budget.fractionUsed * 100);
  const reset = describeReset(budget.periodResetsAt);

  return (
    <div className="flex flex-col gap-1 px-3.5 py-2.5 border-t border-[var(--line)]">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[length:var(--text-2xs)] uppercase tracking-wider text-[var(--ink-3)]">
          Compute budget
        </span>
        <span className={cn('text-[length:var(--text-2xs)] tabular-nums font-medium', TEXT[tone])}>
          {budget.limitReached ? 'spent' : `${pct}%`}
        </span>
      </div>
      <BudgetBar budget={budget} />
      {reset && <span className="text-[length:var(--text-2xs)] text-[var(--ink-3)]">{reset}</span>}
    </div>
  );
}
